// Wire runs — README.md "Runs" and "Gauge by run".

import type { BuildContext } from './context';
import { roundUpHalfMeter } from './copy';

export interface Point2 { x: number; z: number }

/** Origin for every cable run: the AV unit's position when one exists,
 *  else the front-wall midpoint at floor level — README "Runs" / "Origin". */
export function runOrigin(ctx: BuildContext): Point2 {
  if (ctx.avUnit) {
    return { x: ctx.avUnit.entry.position[0], z: ctx.avUnit.entry.position[2] };
  }

  const poly = ctx.roomPolygon;
  if (ctx.listener) {
    const [lx, , lz] = ctx.listener.position;
    // "The front wall is the polygon edge nearest the point one metre ahead
    // of the listener" — forward direction per the doc's facing convention.
    const yaw = ctx.doc.facingYawRad;
    const fwd = yaw != null ? { x: Math.sin(yaw), z: Math.cos(yaw) } : { x: 0, z: -1 };
    const ahead = { x: lx + fwd.x, z: lz + fwd.z };
    return nearestEdgeMidpoint(poly, ahead);
  }
  // "or z = 0 when unsure" — no listener to project a point from; use the
  // edge closest to the z = 0 line as the front wall.
  return nearestEdgeMidpoint(poly, { x: (ctx.doc.dims.Lx ?? 0) / 2, z: 0 });
}

function nearestEdgeMidpoint(poly: readonly { x: number; y: number }[], point: Point2): Point2 {
  let best = { x: point.x, z: 0 };
  let bestDist = Infinity;
  const n = poly.length;
  for (let i = 0; i < n; i++) {
    const a = poly[i];
    const b = poly[(i + 1) % n];
    const d = pointToSegmentDist(point, { x: a.x, z: a.y }, { x: b.x, z: b.y });
    if (d < bestDist) {
      bestDist = d;
      best = { x: (a.x + b.x) / 2, z: (a.y + b.y) / 2 };
    }
  }
  return best;
}

function pointToSegmentDist(p: Point2, a: Point2, b: Point2): number {
  const abx = b.x - a.x;
  const abz = b.z - a.z;
  const len2 = abx * abx + abz * abz;
  if (len2 < 1e-12) return Math.hypot(p.x - a.x, p.z - a.z);
  let t = ((p.x - a.x) * abx + (p.z - a.z) * abz) / len2;
  t = Math.max(0, Math.min(1, t));
  const cx = a.x + t * abx;
  const cz = a.z + t * abz;
  return Math.hypot(p.x - cx, p.z - cz);
}

/** `(|dx| + |dz| + y_speaker) × 1.2 + 1`, rounded up to 0.5 m. */
export function runLength(origin: Point2, speakerPos: readonly [number, number, number]): number {
  const dx = Math.abs(speakerPos[0] - origin.x);
  const dz = Math.abs(speakerPos[2] - origin.z);
  const raw = (dx + dz + speakerPos[1]) * 1.2 + 1;
  return roundUpHalfMeter(raw);
}

/** Total wire length needed per gauge, across the given (channelled,
 *  non-active) speaker ids — the same per-speaker gauge/length math the Buy
 *  list and the room checklist's "Speaker wire" row both need. Shared here
 *  so the two never drift (extract-first rule). */
export function wireRunTotals(ctx: BuildContext, ids: readonly string[]): Map<WireAwg, number> {
  const origin = runOrigin(ctx);
  const totals = new Map<WireAwg, number>();
  for (const id of ids) {
    const entry = ctx.doc.speakers.find((s) => s.id === id);
    if (!entry) continue;
    const product = entry.productId ? ctx.catalog.get(entry.productId) : undefined;
    const active = !!product && product.specs.kind === 'speaker' && product.specs.active.v === true;
    if (active) continue;
    const impedanceOhm = product && product.specs.kind === 'speaker' ? product.specs.impedanceOhm?.v : undefined;
    const len = runLength(origin, entry.position);
    const gauge = wireGauge(impedanceOhm, len);
    totals.set(gauge, (totals.get(gauge) ?? 0) + len);
  }
  return totals;
}

export type WireAwg = 16 | 14 | 12 | 10;

/** Gauge by run — README "Gauge by run" (nominal impedance at 8 Ω when
 *  unknown, keeping wire loss under 5%). Impedances that fall between the
 *  three published buckets (4 / 6 / 8 Ω) are conservatively rounded DOWN to
 *  the next lower bucket (thicker wire sooner) using the same 4.5 Ω split
 *  PWR-02 already uses for its w4/w8 choice, and a 7 Ω split between the 6
 *  and 8 Ω tables (the real catalogue only ships 8 Ω speakers, so this
 *  branch is exercised only by synthetic test variants). Above 8 Ω there is
 *  no published bucket at all; the 8 Ω table is used as-is (it is already
 *  the most permissive one, so this never under-sizes the wire). */
export function wireGauge(impedanceOhm: number | undefined, lengthM: number): WireAwg {
  const z = impedanceOhm ?? 8;
  if (z <= 4.5) {
    if (lengthM <= 7.5) return 16;
    if (lengthM <= 12) return 14;
    if (lengthM <= 19) return 12;
    return 10;
  }
  if (z <= 7) {
    if (lengthM <= 11) return 16;
    if (lengthM <= 18) return 14;
    return 12;
  }
  if (lengthM <= 15) return 16;
  if (lengthM <= 24) return 14;
  return 12;
}
